import React, { useState, useEffect, useCallback, useMemo } from "react";
import { Link } from "react-router-dom";
import { api } from "../api/client";
import { useAuth } from "../contexts/AuthContext";
import { Video, ShieldAlert, Check, X, Flag, Lock, Trash2, Download, AlertTriangle, ChevronDown, ChevronRight, Camera, RotateCcw, Archive, CheckCircle2 } from "lucide-react";
import LoadingSpinner from "../components/LoadingSpinner";

const STATUS_META = {
    new:       { label: "New",       color: "#3b82f6" },
    possible:  { label: "Possible",  color: "#f59e0b" },
    reviewed:  { label: "Possible",  color: "#f59e0b" }, // legacy alias, pre-2026-07-10
    confirmed: { label: "Confirmed", color: "#ef4444" },
    finalised: { label: "Finalised", color: "#15803d" },
    dismissed: { label: "Not fraud", color: "#6b7280" },
    capture_issue:    { label: "Capture issue", color: "#8b5cf6" },
    expired:          { label: "Footage expired", color: "#9ca3af" },   // closed automatically: footage aged off the recorder before capture
    capture_resolved: { label: "Capture fixed", color: "#14b8a6" },
};

// Reasons a capture can be unusable (damaged etc). Free-text comment goes alongside.
const CAPTURE_REASONS = ["Sale not in clip", "Damaged", "Wrong angle/framing", "No footage / black", "Obscured", "Other"];
// Footage not viable: the camera is the problem (not the capture). Any reviewer. Deletes the footage, closes the
// event, lists the camera on the Fraud Report until a senior ticks it actioned. Must match the server list.
// Keep in step with src/api/posfraud-evidence/not-viable.js. "Old clip" is not a camera fault (no camera is listed).
const OLD_CLIP_REASON = "Old clip (cannot be re-pulled)";
// Not fraud needs a reason and a note (server list: src/api/posfraud-evidence/dismiss-reason.js; the API refuses without them).
// 27 Oct 2026: the reviewers' own categories, each with the note they write for it ([reason, template]). Picking one
// fills the notes box with its template while the box is empty or holds an untouched template; never over typed text.
const DISMISS_REASONS = [
    ["Same Item", "Staff scanned the item at <TIME> then cancel the transaction at <TIME>. The item(s) were then re-scanned at <TIME> to complete the sale."],
    ["Switch POS", "Re-rung on the other till at <TIME>"],
    ["Price Check - Staff", "Price check only, scanned item at <TIME> then cancel at <TIME>, goods put back"],
    ["Item Inquiry", "Price check only, scanned item at <TIME> then cancel at <TIME>, customer left without the goods"],
    ["Held Sales", "Scanned item at <TIME> and press held sales to process another customer then cancelled the transaction at <TIME>"],
    ["POS restarted", "Transaction could not be completed as the POS system was restarting at the time of the transaction."],
    ["Operator Error", "Processed a hotkey transaction then cancel it and pressed No sale. No customer present."],
    ["Cash handed back with no till entry (procedure breach)", null],
    ["Other (explain in notes)", null],
];
// Note templates on Flag capture issue and Footage not viable: a choice of notes each (29 Oct 2026, Faye's lists, in her
// order). Same as the server (src/api/posfraud-evidence/dismiss-reason.js).
const CAPTURE_ISSUE_TEMPLATES = ["Video should be pulled from <TIME>", "No camera available to view POS", "Unable to view POS screen, BOH needs to pull camera <INFRASTRUCTURE>", "Damaged video, unable to view POS screen.", "Unable to find the item in the clip", "BOH needs to pull camera <INFRASTRUCTURE>", "No video footage, unable to audit", "POS terminal was partially obscured by the counter setup, limiting visibility of the screen."];
const NOT_VIABLE_TEMPLATES = ["Unable to view POS, camera needs cleaning", "Unable to view POS clearly, needs repositioning", "Unable to view POS clearly, needs focusing"];
const NOTE_TEMPLATES = [...DISMISS_REASONS.map(r => r[1]).filter(Boolean), ...NOT_VIABLE_TEMPLATES, ...CAPTURE_ISSUE_TEMPLATES];
// A placeholder left in the note: the API refuses Not fraud, Footage not viable and Capture issue (notes_placeholder_left).
const PLACEHOLDER_RE = /<TIME>|<INFRASTRUCTURE>/;
const PLACEHOLDER_HINT = "Replace <TIME> / <INFRASTRUCTURE> in the note with what you saw before saving.";
const NOT_VIABLE_REASONS = ["Dirty / needs cleaning", "Needs repositioning", "Needs focusing", OLD_CLIP_REASON];
// Picking a camera reason fills its own note (same fill rule); "Old clip" has none. The note picker offers all three.
const NOT_VIABLE_REASON_TEMPLATES = { "Dirty / needs cleaning": NOT_VIABLE_TEMPLATES[0], "Needs repositioning": NOT_VIABLE_TEMPLATES[1], "Needs focusing": NOT_VIABLE_TEMPLATES[2] };
// Recorders keep about two weeks; detection only requests clips up to 13 days back. Older footage is gone.
const REPULL_MAX_AGE_DAYS = 13;
const ageDays = (local) => { const t = new Date(String(local || "").slice(0, 19)); return Number.isFinite(t.getTime()) ? (Date.now() - t.getTime()) / 86400000 : null; };
const OUTCOME_ACTIONS = [["", "Not recorded"], ["none", "No action"], ["interview", "Interview"], ["warning", "Warning"], ["termination", "Termination"], ["resigned", "Resigned"], ["police", "Police report"], ["other", "Other"]];
// Re-pull refusals from the edge API, in reviewer words.
const REPULL_ERRORS = {
    site_not_on_edge_api: "This site is not on the new capture system yet. Flag the capture issue; a tech will re-pull it.",
    original_request_still_open: "The original capture for this camera is still in progress. Try again once the clip has landed.",
    replacement_limit_needs_investigation: "Three corrected pulls have already been tried for this camera. Flag it for investigation.",
    event_status_not_repullable: "This event's status does not allow a re-pull.",
    clip_start_time_too_far_from_request: "That time is more than 12 hours from what was requested. Check the date and AM/PM.",
    observed_time_too_far_from_sale: "That time is more than 12 hours from the sale. Check the date and AM/PM.",
    no_original_request: "No capture has been requested for this event yet.",
    angle_not_requested: "No capture was requested for that camera.",
    capture_in_flight: "A capture for this event is already in progress. The clip appears here when it lands.",
    clip_exists_use_corrected_repull: "This event already has a clip. Use Re-pull with corrected time instead.",
    capture_not_failed: "The last capture for this event did not fail, so there is nothing to retry.",
    footage_aged_off_recorder: "The recorder no longer has footage from this time. Use Footage not viable with Old clip.",
};

// 'reviewed' was renamed 'possible' — treat both as the possible triage stage.
const isPossible = (s) => s === "possible" || s === "reviewed";
// Clip column / footage box for a ticket with no clip now (the list's clip_state). A dismissed ticket is either one whose
// clip the Not fraud verdict removed, or one dismissed without ever having footage; older verdicts did not record which.
const CLIP_STATE = {
    pending: { short: "pending", long: "Footage pending.", title: "No footage has arrived for this ticket yet" },
    removed_after_verdict: { short: "Clip removed after verdict", long: "Clip removed after verdict: the Not fraud call purged the footage.", title: "This ticket had a clip; the verdict deleted it" },
    dismissed_without_footage: { short: "Dismissed without footage", long: "Dismissed without footage: this ticket never had a clip.", title: "No clip ever arrived for this ticket before it was dismissed" },
    removed_unrecorded: { short: "No clip", long: "No clip on file. This verdict predates clip tracking, so whether it had footage is not recorded.", title: "Dismissed before clip tracking: whether it ever had footage is not recorded" },
    // Managed ticket inside its retention window (27 Oct 2026): the clip is kept in storage but not played on this page.
    held_after_verdict: { short: "Clip kept until purge", long: "Clip kept in storage after the Not fraud call (not played on this page). It is removed automatically on its purge date.", title: "The Not fraud call kept the footage for the retention window" },
};
const clipStateOf = (ev) => CLIP_STATE[ev.clip_state] || (ev.status === "dismissed" ? CLIP_STATE.removed_unrecorded : CLIP_STATE.pending);
// AI review score (shadow mode): the server sends ai_score only to seniors with POSFRAUD_AI_SCORE_DISPLAY on. Bands follow
// the review-line work: 70+ likely, 40-69 unclear, below 40 unlikely.
const aiBand = (score) => score == null ? null : score >= 70 ? { color: "#ef4444", label: "likely" } : score >= 40 ? { color: "#f59e0b", label: "unclear" } : { color: "#6b7280", label: "unlikely" };
function AiScoreBadge({ score, verdict }) {
    const band = aiBand(score);
    if (!band) return <span style={{ fontSize: 11, color: "var(--text-muted)" }}>--</span>;
    return <span title={`AI review: ${score} (${verdict || band.label}). Shadow mode: for seniors only.`} data-testid="ai-score"
        style={{ display: "inline-block", minWidth: 28, textAlign: "center", fontSize: 11, fontWeight: 700, color: "#fff", background: band.color, borderRadius: 10, padding: "1px 7px" }}>{score}</span>;
}
// Repeat-till escalation (28 Oct 2026, pos-fraud docs/REPEAT-TILL.md). The detector writes repeat_count on:
//   CANCEL_NOFUEL  - only a cancel that QUALIFIES (NO SALE on the same register within 90 s, no re-ring of the same goods
//                    with payment), so any value >= 1 means "cancel then No Sale". Value = qualifying cancels on that
//                    register (till) that trading day, this one included. Backtest: ~4 in 5 of these confirmed as fraud
//                    where reviewers confirm frauds, at every count. The API puts them first on the Queue, highest count first.
//   CLEARED_IN_TXN - every ticket (same count of its own type). No review history yet: badge at 3+, informational, no sort.
// NULL = not qualifying, not computed, or the column is missing. A journal fact, not AI, so every reviewer sees it.
// Thresholds match REPEAT_BADGE_MIN in src/api/posfraud-detector-schema.js.
const REPEAT_BADGE_MIN = { CANCEL_NOFUEL: 1, CLEARED_IN_TXN: 3 };
const repeatCountOf = (ev) => {
    const min = ev && REPEAT_BADGE_MIN[ev.event_type];
    if (!min || ev.repeat_count == null || ev.repeat_count === "") return null;
    const n = Math.trunc(Number(ev.repeat_count));
    return Number.isFinite(n) && n >= min ? n : null;
};
const TILL_DAY = "The count is per register (till) per trading day (since the site's last EOD reset), this ticket included, and can span more than one login: check the clerk on each ticket.";
function repeatBadgeText(ev, n) {
    const times = `×${n} on this till that day`;
    if (ev.event_type === "CANCEL_NOFUEL") return n >= 2 ? `Cancel → No Sale ${times}` : "Cancel → No Sale";
    return times;
}
function repeatBadgeTitle(ev, n) {
    if (ev.event_type === "CANCEL_NOFUEL") {
        return "Cancel then No Sale: the sale was cancelled with no payment, a NO SALE opened the drawer on the same register within 90 seconds, "
            + "and the same goods were not rung up again with payment. Historically about 4 in 5 of these tickets were confirmed as fraud, "
            + `where the reviewer confirms frauds. ${n} such cancel${n === 1 ? "" : "s"} on this till that day, journal cancels without a ticket included. ${TILL_DAY}`;
    }
    return `${n} cleared-in-transaction tickets on this till that day. ${TILL_DAY} Informational only until this type has review history: it does not change the Queue order.`;
}
function RepeatBadge({ event }) {
    const n = repeatCountOf(event);
    if (n == null) return null;
    return (
        <span data-testid="repeat-badge" title={repeatBadgeTitle(event, n)}
            style={{ fontSize: 11, fontWeight: 700, padding: "1px 7px", borderRadius: 999, color: "#d97706", background: "#d9770622", border: "1px solid #d9770655", whiteSpace: "nowrap" }}>
            {repeatBadgeText(event, n)}
        </span>
    );
}
// Not fraud needs footage to judge (the API refuses it otherwise). A completed capture-issue re-pull counts: it plays.
const hasFootage = (ev) => !!ev.has_clip || ev.clip_request_status === "completed";
const NOT_FRAUD_NEEDS_CLIP = "Not fraud needs footage to judge, and this ticket has no clip. A failed edge capture is retried automatically: wait for the clip, use Capture problem, or close it as Footage not viable.";

// New detectors trial (19 Oct 2026): shown by their code like every other type (Jamie, 20 Oct); the plain name is the
// tooltip. The event value is the discrepancy, so its column is named for what it measures.
const NEW_TYPE_LABEL = { MISCELLANEOUS: "Misc item (cash)", PRICE_OVERRIDE: "Price override (cash)", CLEARED_IN_TXN: "Items cleared, cash sale" };
const NEW_TYPES = Object.keys(NEW_TYPE_LABEL);
const isNewType = (t) => Object.prototype.hasOwnProperty.call(NEW_TYPE_LABEL, t);
const typeLabel = (t) => t;
const typeTitle = (t) => NEW_TYPE_LABEL[t] || t;
const VALUE_LABEL = { MISCELLANEOUS: "Misc value", PRICE_OVERRIDE: "Discount", CLEARED_IN_TXN: "Cleared value" };
const LINE_KIND = { MISCELLANEOUS: "misc", PRICE_OVERRIDE: "override", CLEARED_IN_TXN: "cleared" };
// Barcode-in-price guard (every event type): a line priced like a keyed barcode is left out of the value and the
// detector says so in the narrative ("line excluded from value: price looks like a keyed barcode").
// The sentence holding the note: a full stop only ends it when a space or the end follows ("$9,300,675.00" does not).
const SENTENCE_CHAR = "(?:[^.!?\\n]|[.!?](?=\\S))";
const BARCODE_NOTE_RE = new RegExp(`${SENTENCE_CHAR}*(?:excluded from (?:the )?value|keyed barcode)${SENTENCE_CHAR}*`, "i");
function barcodeGuardNote(narrative) {
    const m = BARCODE_NOTE_RE.exec(String(narrative || ""));
    return m ? m[0].trim() : null;
}

function fmtMoney(v) {
    if (v == null) return "--";
    const n = Number(v);
    return isNaN(n) ? "--" : "$" + n.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}
function fmtTime(t) {
    if (!t) return "--";
    const d = new Date(t);
    return isNaN(d) ? "--" : d.toLocaleString("en-AU");   // dd/mm/yyyy whatever the browser locale
}
// receipt_local_time is stored as the store's LOCAL wall-clock (from the POS journal), but the SQL
// driver serialises it as a UTC instant ("...Z"), so the browser would shift it by its own tz offset.
// Render the wall-clock components as-is (timeZone UTC undoes that shift). Do NOT use this for
// detected_at/reviewed_at, which are genuine UTC (GETUTCDATE) and should convert to local.
function fmtWallTime(t) {
    if (!t) return "--";
    const d = new Date(t);
    return isNaN(d) ? "--" : d.toLocaleString("en-AU", { timeZone: "UTC" });
}
// Stable, human-friendly reference for an event (derived from its id).
function ticketRef(id) {
    return "PF-" + String(id).padStart(4, "0");
}
// File names for the CSV export, one per view tab.
const EXPORT_TAB_NAME = { new: "Queue", possible: "Possible", confirmed: "Confirmed", finalised: "Finalised", all: "All-tickets",
    dismissed: "Not-fraud", capture_issue: "Capture-issue", not_viable: "Footage-not-viable" };
function saveBlob(blob, filename) {
    const url = URL.createObjectURL(blob); const a = document.createElement("a"); a.href = url; a.download = filename;
    document.body.appendChild(a); a.click(); a.remove(); setTimeout(() => URL.revokeObjectURL(url), 1000);
}

function DownloadBtn({ onDownload }) {
    const [busy, setBusy] = useState(false);
    if (!onDownload) return null;
    return (
        <button
            onClick={async () => { setBusy(true); try { await onDownload(); } catch (e) { alert("Download failed: " + (e.message || e)); } finally { setBusy(false); } }}
            disabled={busy}
            style={{
                marginTop: 8, display: "inline-flex", alignItems: "center", gap: 6, padding: "8px 14px",
                fontSize: 13, fontWeight: 600, borderRadius: 6, border: "1px solid var(--border)",
                background: "var(--bg-hover)", color: "var(--text-secondary)", cursor: busy ? "wait" : "pointer",
            }}
        >
            <Download size={14} /> {busy ? "Preparing..." : "Download clip"}
        </button>
    );
}

function VideoPlayer({ src, onDownload, maxWidth = 720 }) {
    // Soft detection: never hide the player. Some browsers/machines CAN decode
    // H.265 (e.g. Edge / Windows with the HEVC extension), so always let <video>
    // try. If it fires an error we show a non-blocking warning + the download
    // option, rather than replacing a player that may actually be working.
    // On a player error, ask the server whether the clip file exists (20 Oct 2026): a missing file (404/403) used to
    // show the H.265 note too, which sent reviewers to VLC for a clip that is not there.
    const [warn, setWarn] = useState(null);   // null | "codec" | "missing"
    const onError = () => {
        setWarn("codec");
        api.posfraud.clipStatus(src)
            .then(r => { if (r && (r.status === 404 || r.status === 403)) setWarn("missing"); })
            .catch(() => {});
    };
    return (
        <div>
            <video
                src={src}
                controls
                onError={onError}
                style={{ width: "100%", maxWidth, borderRadius: 8, background: "#000" }}
            />
            {warn === "missing" && (
                <div data-clip-warning="missing" style={{ marginTop: 6, fontSize: 12, color: "#ef4444", display: "flex", alignItems: "center", gap: 6 }}>
                    <AlertTriangle size={13} /> Clip file not found in storage: it was deleted, has not finished uploading, or this copy of the site has no footage. Flag a capture issue if it should be there.
                </div>
            )}
            {warn === "codec" && (
                <div data-clip-warning="codec" style={{ marginTop: 6, fontSize: 12, color: "#f59e0b", display: "flex", alignItems: "center", gap: 6 }}>
                    <AlertTriangle size={13} /> Your browser flagged this clip as possibly unplayable (H.265/HEVC). If it isn't playing above, download it to view in VLC.
                </div>
            )}
            <DownloadBtn onDownload={onDownload} />
        </div>
    );
}

function StatusBadge({ status, notViable }) {
    const m = notViable ? { label: "Footage not viable", color: "#f59e0b" } : (STATUS_META[status] || { label: status, color: "#6b7280" });
    return (
        <span style={{
            fontSize: 11, fontWeight: 700, padding: "2px 8px", borderRadius: 999,
            color: m.color, background: `${m.color}22`, border: `1px solid ${m.color}55`, whiteSpace: "nowrap",
        }}>
            {m.label}
        </span>
    );
}

// Review trail (28 Oct 2026): who made each step, kept apart from reviewed_by (which is the latest action only).
// The API sends NULLs until sql/Add-PosFraudReviewerTrail.sql has run, and for steps that were not recorded (verdicts
// before the trail existed that the backfill could not reconstruct); the block is hidden when there is nothing to show.
// A step the backfill rebuilt from history (trail_reconstructed) is marked as such, so it never reads as a recorded one.
const TRAIL_DECISION = { possible: "Possible", confirmed: "Confirmed fraud", dismissed: "Not fraud", footage_not_viable: "Footage not viable" };
function ReviewTrail({ event }) {
    if (!event.initial_reviewed_by && !event.confirmed_by && !event.finalised_by) return null;
    const rebuilt = String(event.trail_reconstructed || "").split(",").map(x => x.trim());
    const steps = [{ key: "initial", label: "Initial auditor", by: event.initial_reviewed_by, at: event.initial_reviewed_at,
        note: event.initial_verdict ? (TRAIL_DECISION[event.initial_verdict] || event.initial_verdict) : null }];
    if (event.confirmed_by || ["confirmed", "finalised"].includes(event.status)) steps.push({ key: "confirmed", label: "Confirmed by", by: event.confirmed_by, at: event.confirmed_at });
    if (event.finalised_by || event.status === "finalised") steps.push({ key: "finalised", label: "Finalised by", by: event.finalised_by, at: event.finalised_at });
    return (
        <div data-testid="review-trail" style={{ fontSize: 12, color: "var(--text-secondary)", marginBottom: 10, padding: "6px 8px", border: "1px solid var(--border)", borderRadius: 6 }}>
            <div style={{ fontWeight: 600, marginBottom: 4 }}>Review trail</div>
            <div style={{ display: "flex", flexWrap: "wrap", alignItems: "baseline", gap: 6 }}>
                {steps.map((s, i) => (
                    <React.Fragment key={s.key}>
                        {i > 0 && <span aria-hidden="true" style={{ color: "var(--text-muted)" }}>&rarr;</span>}
                        <span data-trail-step={s.key}>
                            {s.label} {s.by ? <b>{s.by}</b> : <i style={{ color: "var(--text-muted)" }}>not recorded</i>}
                            {s.note ? " (" + s.note + ")" : ""}{s.by && s.at ? " · " + fmtTime(s.at) : ""}
                            {s.by && rebuilt.includes(s.key) ? <i data-trail-reconstructed="1" style={{ color: "var(--text-muted)" }}
                                title="Filled in afterwards from the request log and review records, not recorded at the time; the time is approximate"> (reconstructed from history)</i> : null}
                        </span>
                    </React.Fragment>
                ))}
            </div>
        </div>
    );
}

export default function PosFraud() {
    const [events, setEvents] = useState([]);
    const [counts, setCounts] = useState({});
    const [loading, setLoading] = useState(true);
    const [expanded, setExpanded] = useState(null);
    const [status, setStatus] = useState("new");
    const [site, setSite] = useState("");
    const [eventType, setEventType] = useState("");
    const [from, setFrom] = useState("");
    const [to, setTo] = useState("");
    const [showPending, setShowPending] = useState(false);  // include events still awaiting a clip (off by default)
    const [showExpired, setShowExpired] = useState(false);  // include closed events whose footage aged off before capture
    const [page, setPage] = useState(1);
    const [total, setTotal] = useState(0);
    const [ticket, setTicket] = useState("");          // ticket number search: PF-24967 or 24967, any status
    const PAGE_SIZE = 200;
    const [sortKey, setSortKey] = useState("date");   // 'date' (event time) | 'site' | 'value' | 'type' | 'ai' (seniors, flag on)
    const [aiEnabled, setAiEnabled] = useState(false);  // the list response says whether AI scores are shown to this user
    const [sortDir, setSortDir] = useState("desc");    // 'asc' | 'desc'

    // Header click: toggle direction if already sorting by this key, else switch key
    // (newest-first for date, A->Z for the rest).
    const toggleSort = (key) => {
        if (sortKey === key) setSortDir(d => (d === "asc" ? "desc" : "asc"));
        else { setSortKey(key); setSortDir(key === "date" || key === "value" || key === "ai" ? "desc" : "asc"); }
    };

    // Export: every row of the current tab with the current filters and sort, not just this page.
    const [exporting, setExporting] = useState(false);

    // Non-status scope shared by the list and the per-view tab counts.
    const scopeParams = useCallback(() => {
        const p = {};
        if (site) p.site = site;
        if (eventType) p.event_type = eventType;
        if (from) p.from = from;
        if (to) p.to = to;
        if (showPending) p.include_pending = "true";
        if (showExpired) p.include_expired = "true";
        return p;
    }, [site, eventType, from, to, showPending, showExpired]);

    const loadData = useCallback(async () => {
        setLoading(true);
        try {
            const params = scopeParams();
            if (status) params.status = status;
            params.sort = sortKey; params.dir = sortDir;
            params.page = page; params.page_size = PAGE_SIZE;
            if (ticket.trim()) params.ticket = ticket.trim();
            const [data, cts] = await Promise.all([
                api.posfraud.list(params),
                api.posfraud.counts(scopeParams()).catch(() => null),
            ]);
            setEvents(data.events || []); setTotal(data.total || 0); setAiEnabled(!!data.ai_enabled);
            if (cts) setCounts(cts.counts || {});
        } catch (e) {
            console.error("Failed to load POS fraud events", e);
            setEvents([]);
        } finally {
            setLoading(false);
        }
    }, [status, scopeParams, sortKey, sortDir, page, ticket]);

    useEffect(() => { loadData(); }, [loadData]);
    const exportCsv = async () => {
        setExporting(true);
        try {
            const params = scopeParams();
            if (status) params.status = status;
            params.sort = sortKey; params.dir = sortDir;
            if (ticket.trim()) params.ticket = ticket.trim();
            const name = EXPORT_TAB_NAME[ticket.trim() ? "all" : status] || "Tickets";
            saveBlob(await api.posfraud.exportCsv(params), `POS-Fraud-${name}-${new Date().toISOString().slice(0, 10)}.csv`);
        } catch (e) { alert("Export failed: " + (e.message || e)); }
        finally { setExporting(false); }
    };
    // Any change of view, filter or sort starts again from page 1.
    useEffect(() => { setPage(1); }, [status, site, eventType, from, to, showPending, showExpired, sortKey, sortDir, ticket]);

    // Event-type options come from whatever is currently loaded, plus the new detector types (always offered, so a
    // reviewer can find them before any has loaded).
    const eventTypes = useMemo(
        () => [...new Set([...NEW_TYPES, ...events.map(e => e.event_type).filter(Boolean)])].sort(),
        [events]
    );
    // Choosing a new detector type sorts by its value, biggest first (a bigger discount / cleared value is worse).
    const chooseEventType = (t) => {
        setEventType(t);
        if (isNewType(t)) { setSortKey("value"); setSortDir("desc"); }
    };

    const thStyle = {
        padding: "8px 12px", fontSize: 11, fontWeight: 600, textTransform: "uppercase",
        letterSpacing: 0.5, color: "var(--text-secondary)", borderBottom: "1px solid var(--border)",
        textAlign: "left", whiteSpace: "nowrap",
    };
    const tdStyle = { padding: "8px 12px", fontSize: 13, borderBottom: "1px solid var(--border-light, rgba(255,255,255,0.05))", verticalAlign: "top" };

    // Clickable, sortable column header. Shows the active direction, or a faint hint when inactive.
    const sortTh = (label, key) => (
        <th style={{ ...thStyle, cursor: "pointer", userSelect: "none" }} onClick={() => toggleSort(key)}
            title={`Sort by ${label.toLowerCase()}`}>
            {label}
            <span style={{ marginLeft: 4, opacity: sortKey === key ? 1 : 0.3 }}>
                {sortKey === key ? (sortDir === "asc" ? "↑" : "↓") : "↕"}
            </span>
        </th>
    );

    return (
        <div>
            <div className="page-header">
                <h2 style={{ display: "flex", alignItems: "center", gap: 8 }}>
                    <ShieldAlert size={22} color="#ef4444" /> POS Fraud
                </h2>
                <p style={{ color: "var(--text-secondary)", fontSize: 13 }}>
                    AI-flagged POS-fraud candidates for two-layer human review. First pass clears the fluff
                    ("Not fraud" — evidence is deleted) or bubbles candidates up ("Possible" — evidence is
                    vaulted). Second pass assesses Possibles and locks them as confirmed fraud. Confirmed
                    cases are marked "Finalised" once dealt with in real life (staff actioned etc.).
                </p>
            </div>

            {/* Process flow: the main path left to right, the exits underneath. Each node filters the list. */}
            {(() => {
                const node = (key, label, color) => (
                    <button key={key} onClick={() => setStatus(key)} title={`Show ${label.toLowerCase()}`}
                        style={{ padding: "7px 14px", fontSize: 12.5, fontWeight: 600, cursor: "pointer", borderRadius: 6, border: `1px solid ${status === key ? color : "var(--border)"}`,
                            background: status === key ? color : "var(--bg-hover, #f5f5f5)", color: status === key ? "#fff" : "var(--text-secondary)", transition: "background 0.12s" }}>
                        {label}{counts[key] != null ? ` (${counts[key]})` : ""}
                    </button>);
                const arrow = <span style={{ color: "var(--text-muted)", fontSize: 16, padding: "0 4px" }}>→</span>;
                const gap = <span style={{ padding: "0 3px" }} />;
                return (
                    <div style={{ marginBottom: 14, display: "flex", flexDirection: "column", gap: 6 }}>
                        <div style={{ display: "flex", alignItems: "center", flexWrap: "wrap", gap: 2 }}>
                            {node("new", "Queue", "#6b7280")}{arrow}{node("possible", "Possible", "#d97706")}{arrow}{node("confirmed", "Confirmed fraud", "#ef4444")}{arrow}{node("finalised", "Finalised", "#15803d")}
                            <span style={{ marginLeft: "auto" }}>{node("all", "All tickets", "#374151")}</span>
                        </div>
                        <div style={{ display: "flex", alignItems: "center", flexWrap: "wrap", gap: 2, paddingLeft: 28 }}>
                            <span style={{ color: "var(--text-muted)", fontSize: 12, marginRight: 6 }}>↳ leaves the flow as</span>
                            {node("dismissed", "Not fraud", "#6b7280")}{gap}{node("capture_issue", "Capture issue", "#8b5cf6")}{gap}{node("not_viable", "Footage not viable", "#f59e0b")}
                        </div>
                    </div>);
            })()}

            {/* Filters */}
            <div style={{ display: "flex", gap: 8, marginBottom: 16, alignItems: "center", flexWrap: "wrap" }}>
                <label style={{ fontSize: 12, color: "var(--text-secondary)" }}>Ticket
                    <input className="filter-select" value={ticket} onChange={e => setTicket(e.target.value)}
                        placeholder="PF-24967" title="Find one ticket by number, whatever its status" style={{ fontSize: 13, maxWidth: 110, marginLeft: 4 }} />
                </label>
                <label style={{ fontSize: 12, color: "var(--text-secondary)" }}>Store
                    <input className="filter-select" value={site} onChange={e => setSite(e.target.value)}
                        placeholder="Site ID..." style={{ fontSize: 13, maxWidth: 110, marginLeft: 4 }} />
                </label>
                <select className="filter-select" value={eventType} onChange={e => chooseEventType(e.target.value)} style={{ fontSize: 13 }}>
                    <option value="">All event types</option>
                    {eventTypes.map(t => <option key={t} value={t} title={typeTitle(t)}>{typeLabel(t)}</option>)}
                </select>
                <label style={{ fontSize: 12, color: "var(--text-secondary)" }}>From
                    <input type="date" className="filter-select" value={from} onChange={e => setFrom(e.target.value)} style={{ fontSize: 13, marginLeft: 4 }} />
                </label>
                <label style={{ fontSize: 12, color: "var(--text-secondary)" }}>To
                    <input type="date" className="filter-select" value={to} onChange={e => setTo(e.target.value)} style={{ fontSize: 13, marginLeft: 4 }} />
                </label>
                <label style={{ fontSize: 12, color: "var(--text-secondary)", display: "flex", alignItems: "center", gap: 5, cursor: "pointer" }}
                    title="Include open tickets still waiting for footage (shown as 'pending' in the Clip column). Not fraud tickets are always listed, with or without a clip.">
                    <input type="checkbox" checked={showPending} onChange={e => setShowPending(e.target.checked)} />
                    Show pending clips
                </label>
                <label style={{ fontSize: 12, color: "var(--text-secondary)", display: "flex", alignItems: "center", gap: 5, cursor: "pointer" }}
                    title="Include events closed automatically because the footage aged off the recorder before it could be captured (shown under All)">
                    <input type="checkbox" checked={showExpired} onChange={e => { setShowExpired(e.target.checked); if (e.target.checked) setShowPending(true); }} />
                    Show expired
                </label>
                <span style={{ fontSize: 12, color: "var(--text-secondary)", marginLeft: "auto", display: "flex", alignItems: "center", gap: 6 }}>
                    {total > PAGE_SIZE ? (<>
                        <button onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page <= 1} className="filter-select" style={{ fontSize: 12, cursor: page <= 1 ? "default" : "pointer" }}>‹ Prev</button>
                        Page {page} of {Math.ceil(total / PAGE_SIZE)}
                        <button onClick={() => setPage(p => p + 1)} disabled={page >= Math.ceil(total / PAGE_SIZE)} className="filter-select" style={{ fontSize: 12, cursor: page >= Math.ceil(total / PAGE_SIZE) ? "default" : "pointer" }}>Next ›</button>
                        · {total.toLocaleString()} events
                    </>) : `${total || events.length} events`}
                    <button onClick={exportCsv} disabled={exporting || loading || !total} className="filter-select"
                        title="Download every event in this view (all pages) as a CSV, with the filters and sort above"
                        style={{ fontSize: 12, display: "flex", alignItems: "center", gap: 4, cursor: exporting || loading || !total ? "default" : "pointer" }}>
                        <Download size={13} /> {exporting ? "Exporting..." : "Export CSV"}
                    </button>
                </span>
            </div>

            {loading ? (
                <LoadingSpinner message="Loading POS fraud events..." />
            ) : (
                <div className="card" style={{ overflow: "auto" }}>
                    <table style={{ width: "100%", borderCollapse: "collapse" }}>
                        <thead>
                            <tr>
                                <th style={thStyle}>Ticket</th>
                                {sortTh("Site", "site")}
                                {sortTh("Event time", "date")}
                                {sortTh("Type", "type")}
                                {sortTh(VALUE_LABEL[eventType] || "Value", "value")}
                                {aiEnabled && sortTh("AI", "ai")}
                                <th style={thStyle}>Summary</th>
                                <th style={thStyle}>Status</th>
                                <th style={thStyle}>Clip</th>
                            </tr>
                        </thead>
                        <tbody>
                            {events.length === 0 && (
                                <tr><td colSpan={aiEnabled ? 9 : 8} style={{ ...tdStyle, textAlign: "center", color: "var(--text-secondary)", padding: 32 }}>
                                    No POS fraud events for this filter
                                </td></tr>
                            )}
                            {events.map(ev => {
                                const isExpanded = expanded === ev.id;
                                return (
                                    <React.Fragment key={ev.id}>
                                        <tr style={{ cursor: "pointer" }} onClick={() => setExpanded(isExpanded ? null : ev.id)}>
                                            <td style={{ ...tdStyle, fontFamily: "monospace", fontWeight: 700, whiteSpace: "nowrap" }}>{ticketRef(ev.id)}</td>
                                            <td style={tdStyle}>
                                                <Link to={`/store/${ev.site_id}`} style={{ color: "var(--accent)", fontWeight: 600 }}
                                                    onClick={e => e.stopPropagation()}>{ev.site_id}</Link>
                                            </td>
                                            <td style={tdStyle}>{ev.receipt_local_time ? fmtWallTime(ev.receipt_local_time) : fmtTime(ev.detected_at)}</td>
                                            <td style={tdStyle}>
                                                <span style={{ fontSize: 12, fontWeight: 600 }} title={typeTitle(ev.event_type)}>{typeLabel(ev.event_type)}</span>
                                                {repeatCountOf(ev) != null && <div style={{ marginTop: 3 }}><RepeatBadge event={ev} /></div>}
                                            </td>
                                            <td style={tdStyle}>{fmtMoney(ev.value)}</td>
                                            {aiEnabled && <td style={tdStyle}><AiScoreBadge score={ev.ai_score} verdict={ev.ai_verdict} /></td>}
                                            <td style={{ ...tdStyle, maxWidth: 360, color: "var(--text-secondary)" }}>
                                                {ev.description || (ev.narrative ? ev.narrative.slice(0, 90) + (ev.narrative.length > 90 ? "..." : "") : "--")}
                                            </td>
                                            <td style={tdStyle}><StatusBadge status={ev.status} notViable={!!ev.not_viable_reason} /></td>
                                            <td style={tdStyle}>
                                                <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                                                    {ev.has_clip
                                                        ? <span style={{ fontSize: 11, color: "#22c55e", display: "flex", alignItems: "center", gap: 3 }}><Video size={13} /> {ev.clip_count > 1 ? `${ev.clip_count} clips` : "Clip"}</span>
                                                        : <span style={{ fontSize: 11, color: "var(--text-muted)" }} title={clipStateOf(ev).title}>{clipStateOf(ev).short}</span>}
                                                    {isExpanded ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
                                                </div>
                                            </td>
                                        </tr>
                                        {isExpanded && (
                                            <tr>
                                                <td colSpan={8} style={{ padding: "0 12px 16px", background: "var(--bg-card)" }}>
                                                    <DetailBoundary key={ev.id}><FraudDetail event={ev} onDone={loadData} /></DetailBoundary>
                                                </td>
                                            </tr>
                                        )}
                                    </React.Fragment>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
            )}
        </div>
    );
}

// Action button style, shared by the ticket panel and its sub-panels (Outcome, Staff). Module level on purpose:
// a sub-panel referencing a helper local to FraudDetail threw "btn is not defined" and blanked the whole page.
const btnStyle = (bg, busy) => ({
    padding: "8px 16px", fontSize: 13, fontWeight: 600, borderRadius: 6, border: "none",
    color: "#fff", background: bg, cursor: busy ? "wait" : "pointer", opacity: busy ? 0.6 : 1,
    display: "inline-flex", alignItems: "center", gap: 6,
});

// Real-world outcome of a confirmed or finalised case: what was done, what came back, when. Senior only. The HR
// reference stays free text until HR names their system and identifier.
function OutcomePanel({ outcome, setOutcome, save, busy, msg }) {
    const field = { fontSize: 12, padding: "6px 8px", borderRadius: 6, border: "1px solid var(--border)", background: "var(--bg-main)", color: "var(--text-primary)" };
    return (
        <div style={{ marginTop: 12, paddingTop: 10, borderTop: "1px solid var(--border)" }}>
            <div style={{ fontSize: 12, fontWeight: 600, color: "var(--text-secondary)", marginBottom: 6 }}>Outcome record (for the Fraud Report)</div>
            <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center" }}>
                <select value={outcome.action_taken} onChange={e => setOutcome(o => ({ ...o, action_taken: e.target.value }))} className="filter-select" style={{ fontSize: 12 }}>
                    {OUTCOME_ACTIONS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                </select>
                <label style={{ fontSize: 12, color: "var(--text-secondary)" }}>Recovered $ <input type="number" min="0" step="0.01" value={outcome.amount_recovered} onChange={e => setOutcome(o => ({ ...o, amount_recovered: e.target.value }))} style={{ ...field, width: 100, marginLeft: 4 }} /></label>
                <label style={{ fontSize: 12, color: "var(--text-secondary)" }}>Actioned on <input type="date" value={outcome.actioned_at} onChange={e => setOutcome(o => ({ ...o, actioned_at: e.target.value }))} style={{ ...field, marginLeft: 4 }} /></label>
                <input placeholder="HR reference (optional)" value={outcome.hr_reference} onChange={e => setOutcome(o => ({ ...o, hr_reference: e.target.value }))} style={{ ...field, width: 180 }} />
                <button onClick={save} disabled={busy} style={btnStyle("#3b82f6", busy)}><CheckCircle2 size={14} /> Save outcome</button>
                <button disabled title="HR link: available once HR confirms the system and identifier to store." style={{ ...btnStyle("#9ca3af", false), cursor: "not-allowed" }}>Open in HR</button>
            </div>
            {msg && <div style={{ fontSize: 11, color: "#15803d", marginTop: 4 }}>{msg}</div>}
        </div>
    );
}

// Staff attribution (Deputy). The roster is a hint, not a filter: everyone on the site's staff list is offered, those
// clocked on at the sale time first and badged. Only a senior can set or clear it, and "Unknown / not listed" is a
// valid end state. Register and till login stay in the ticket header: they are till facts, not a person.
function StaffPanel({ event, isSenior, onDone }) {
    const [opts, setOpts] = useState(null);     // null = loading
    const [loadErr, setLoadErr] = useState(null);
    const [choice, setChoice] = useState(event.deputy_employee_id ? String(event.deputy_employee_id) : "");
    const [busy, setBusy] = useState(false);
    const [msg, setMsg] = useState(null);
    useEffect(() => {
        let alive = true; setOpts(null); setLoadErr(null);
        if (!isSenior) return () => { alive = false; };
        api.posfraud.staffOptions(event.id)
            .then(d => { if (alive) setOpts(d); })
            .catch(e => { if (alive) { setOpts({ staff: [], shifts_known: false, company: null }); setLoadErr(e.message); } });
        return () => { alive = false; };
    }, [event.id, isSenior]);
    const save = async () => {
        setBusy(true); setMsg(null);
        try {
            const picked = (opts?.staff || []).find(s => String(s.id) === choice);
            await api.posfraud.staffLink(event.id, picked ? { employee_id: picked.id, name: picked.name, on_roster: picked.on_duty } : { employee_id: null });
            setMsg(picked ? `Attributed to ${picked.name}.` : "Staff link cleared."); onDone && onDone();
        } catch (e) { setMsg(e.message); } finally { setBusy(false); }
    };
    const staff = opts?.staff || [];
    const onDuty = staff.filter(s => s.on_duty), others = staff.filter(s => !s.on_duty);
    const label = (s) => `${s.name}${s.covering ? " (from another site)" : ""}`;
    return (
        <div style={{ marginTop: 10, marginBottom: 10, paddingTop: 10, borderTop: "1px solid var(--border)" }}>
            <div style={{ fontSize: 12, fontWeight: 600, color: "var(--text-secondary)", marginBottom: 4 }}>Staff member (Deputy)</div>
            {event.staff_name
                ? <div style={{ fontSize: 12, marginBottom: 6 }}>Attributed to <b>{event.staff_name}</b>{event.staff_on_roster === true ? " (clocked on at the time)" : event.staff_on_roster === false ? " (not on a timesheet at the time)" : ""}{event.staff_linked_by ? ` by ${event.staff_linked_by}` : ""}.</div>
                : !isSenior && <div style={{ fontSize: 12, color: "var(--text-muted)" }}>Not attributed. A senior sets this.</div>}
            {isSenior && (opts === null ? <div style={{ fontSize: 12, color: "var(--text-muted)" }}>Loading staff from Deputy...</div> : (<>
                <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
                    <select className="filter-select" value={choice} onChange={e => setChoice(e.target.value)} style={{ fontSize: 12, minWidth: 280 }}>
                        <option value="">Unknown / not listed</option>
                        {onDuty.length > 0 && <optgroup label={`Clocked on at the time (${opts.source === "roster" ? "roster" : "timesheets"})`}>{onDuty.map(s => <option key={s.id} value={s.id}>{label(s)}</option>)}</optgroup>}
                        {others.length > 0 && <optgroup label={onDuty.length ? "Other store staff" : "Store staff"}>{others.map(s => <option key={s.id} value={s.id}>{label(s)}</option>)}</optgroup>}
                    </select>
                    <button onClick={save} disabled={busy} style={btnStyle("#3b82f6", busy)}><CheckCircle2 size={14} /> Save staff</button>
                </div>
                <div style={{ fontSize: 11, color: "var(--text-muted)", marginTop: 4 }}>
                    {loadErr ? `Deputy could not be read: ${loadErr}` : !opts.company ? "This site is not set up in Deputy; nothing to choose from." : opts.shifts_known ? "The roster is a hint only: anyone on the list can be chosen." : "Deputy has no shift data for this day, so the list is the whole store staff."}
                </div>
                {msg && <div style={{ fontSize: 11, color: "#15803d", marginTop: 4 }}>{msg}</div>}
            </>))}
        </div>
    );
}

// A render error inside one ticket's panel must not blank the whole page: show the message where the panel was.
class DetailBoundary extends React.Component {
    constructor(props) { super(props); this.state = { error: null }; }
    static getDerivedStateFromError(error) { return { error }; }
    render() {
        if (this.state.error) return <div style={{ padding: 12, color: "#ef4444", fontSize: 12 }}>This ticket's panel failed to render: {String(this.state.error.message || this.state.error)}. Copy this message to Jamie.</div>;
        return this.props.children;
    }
}

// New detectors trial: what the detector found, from the event's stored lines (GET /:id/lines) and its cash columns.
// Only rendered for MISCELLANEOUS, PRICE_OVERRIDE and CLEARED_IN_TXN; every other type's panel is unchanged.
// A database without the new columns gives NULL prices/cash: those cells show "--" and the panel still renders.
const num = (v) => (v == null || v === "" || !Number.isFinite(Number(v)) ? null : Number(v));
// Line discrepancy: the stored discount, else worked out from the unit prices (override: (shelf - charged) x qty).
function lineDiscount(l) {
    if (num(l.discount) != null) return num(l.discount);
    const shelf = num(l.shelf_price), charged = num(l.charged_price), qty = num(l.qty) ?? 1;
    return shelf != null && charged != null ? Math.round((shelf - charged) * qty * 100) / 100 : null;
}
function pctOff(l) {
    const shelf = num(l.shelf_price), qty = num(l.qty) ?? 1, d = lineDiscount(l);
    if (shelf == null || d == null || shelf * qty <= 0) return null;
    return Math.round((d / (shelf * qty)) * 1000) / 10;
}
const productCell = (l) => (
    <span>{l.product_name || l.inventory_code || "(no name)"}{l.product_name && l.inventory_code ? <span style={{ color: "var(--text-muted)", fontSize: 11 }}> · {l.inventory_code}</span> : null}</span>
);
function Fact({ label, value, title, strong }) {
    return (
        <div title={title} style={{ minWidth: 110 }}>
            <div style={{ fontSize: 10, fontWeight: 600, textTransform: "uppercase", letterSpacing: 0.5, color: "var(--text-muted)" }}>{label}</div>
            <div style={{ fontSize: strong ? 16 : 14, fontWeight: strong ? 700 : 600 }}>{value}</div>
        </div>
    );
}
function TierBadge({ tier }) {
    if (tier !== "pocketed" && tier !== "value") return null;
    const pocketed = tier === "pocketed";
    const color = pocketed ? "#ef4444" : "#d97706";
    return (
        <span data-tier={tier} title={pocketed ? "Exact full tender: the change recorded equals the value of the items cleared (cash only)" : "Cleared value over the trial threshold"}
            style={{ fontSize: 11, fontWeight: 700, padding: "2px 8px", borderRadius: 999, color, background: `${color}22`, border: `1px solid ${color}55`, whiteSpace: "nowrap" }}>
            {pocketed ? "Change matches cleared items" : "High cleared value"}
        </span>
    );
}
function DetectorDetail({ event }) {
    const [lines, setLines] = useState(null);     // null = loading
    const [failed, setFailed] = useState(false);
    useEffect(() => {
        let alive = true; setLines(null); setFailed(false);
        api.posfraud.lines(event.id)
            .then(d => { if (alive) setLines(d.lines || []); })
            .catch(() => { if (alive) { setLines([]); setFailed(true); } });
        return () => { alive = false; };
    }, [event.id]);
    const type = event.event_type;
    const rows = (lines || []).filter(l => l.line_kind === LINE_KIND[type]);
    const th = { padding: "5px 8px", fontSize: 10.5, fontWeight: 600, textTransform: "uppercase", letterSpacing: 0.4, color: "var(--text-secondary)", borderBottom: "1px solid var(--border)", textAlign: "left", whiteSpace: "nowrap" };
    const td = { padding: "5px 8px", fontSize: 12.5, borderBottom: "1px solid var(--border-light, rgba(255,255,255,0.05))" };
    const tdr = { ...td, textAlign: "right", fontVariantNumeric: "tabular-nums" };
    const thr = { ...th, textAlign: "right" };
    const tendered = num(event.cash_tendered), change = num(event.change_given);
    const cashFacts = (<>
        <Fact label="Cash tendered" value={fmtMoney(tendered)} />
        <Fact label="Change given" value={fmtMoney(change)} />
    </>);
    const sum = (f) => rows.reduce((a, l) => a + (f(l) ?? 0), 0);
    let title, facts, table;
    if (type === "PRICE_OVERRIDE") {
        const sorted = [...rows].sort((a, b) => (lineDiscount(b) ?? -Infinity) - (lineDiscount(a) ?? -Infinity));
        title = "Price override, cash sale";
        facts = (<>
            <Fact label="Total discount" strong value={fmtMoney(event.value ?? (rows.length ? sum(lineDiscount) : null))} />
            <Fact label="Lines overridden" value={lines === null ? "--" : rows.length} />
            {cashFacts}
        </>);
        table = (
            <table style={{ borderCollapse: "collapse", width: "100%" }}>
                <thead><tr><th style={th}>Product</th><th style={thr}>Qty</th><th style={thr}>Shelf price</th><th style={thr}>Charged price</th><th style={thr}>Discount $</th><th style={thr}>% off</th></tr></thead>
                <tbody>{sorted.map((l, i) => {
                    const p = pctOff(l);
                    return (
                        <tr key={l.id ?? i}>
                            <td style={td}>{productCell(l)}</td>
                            <td style={tdr}>{num(l.qty) ?? "--"}</td>
                            <td style={tdr}>{fmtMoney(l.shelf_price)}</td>
                            <td style={tdr}>{fmtMoney(l.charged_price)}</td>
                            <td style={{ ...tdr, fontWeight: 700 }}>{fmtMoney(lineDiscount(l))}</td>
                            <td style={{ ...tdr, color: p != null && p >= 50 ? "#ef4444" : undefined, fontWeight: p != null && p >= 50 ? 700 : undefined }}>{p == null ? "--" : `${p}%`}</td>
                        </tr>);
                })}</tbody>
            </table>);
    } else if (type === "CLEARED_IN_TXN") {
        const charged = tendered != null && change != null ? Math.round((tendered - change) * 100) / 100 : null;
        title = <span style={{ display: "inline-flex", alignItems: "center", gap: 8 }}>Items cleared, then a cash sale <TierBadge tier={event.detector_tier} /></span>;
        facts = (<>
            <Fact label="Cleared value" strong value={fmtMoney(event.value ?? (rows.length ? sum(l => num(l.discount) ?? num(l.shelf_price)) : null))} />
            <Fact label="Charged total" value={fmtMoney(charged)} title="Cash tendered less change given. The card part of a split payment is not included." />
            {cashFacts}
        </>);
        table = (
            <table style={{ borderCollapse: "collapse", width: "100%" }}>
                <thead><tr><th style={th}>Cleared item</th><th style={thr}>Shelf price</th></tr></thead>
                <tbody>{rows.map((l, i) => (
                    <tr key={l.id ?? i}><td style={td}>{productCell(l)}</td><td style={tdr}>{fmtMoney(l.shelf_price ?? l.discount)}</td></tr>
                ))}</tbody>
            </table>);
    } else {
        const lineValue = (l) => num(l.sales) ?? num(l.discount) ?? (num(l.charged_price) != null ? num(l.charged_price) * (num(l.qty) ?? 1) : null);
        title = "Misc / open-key item, cash sale";
        facts = (<>
            <Fact label="Misc value" strong value={fmtMoney(event.value ?? (rows.length ? sum(lineValue) : null))} />
            <Fact label="Misc lines" value={lines === null ? "--" : rows.length} />
            {cashFacts}
        </>);
        table = (
            <table style={{ borderCollapse: "collapse", width: "100%" }}>
                <thead><tr><th style={th}>Misc line</th><th style={thr}>Qty</th><th style={thr}>Value</th></tr></thead>
                <tbody>{rows.map((l, i) => (
                    <tr key={l.id ?? i}><td style={td}>{productCell(l)}</td><td style={tdr}>{num(l.qty) ?? "--"}</td><td style={{ ...tdr, fontWeight: 700 }}>{fmtMoney(lineValue(l))}</td></tr>
                ))}</tbody>
            </table>);
    }
    return (
        <div data-detector={type} style={{ marginTop: 12, padding: 12, borderRadius: 8, background: "var(--bg-secondary)", border: "1px solid var(--border)" }}>
            <div style={{ fontSize: 13, fontWeight: 700, marginBottom: 8 }}>{title}</div>
            <div style={{ display: "flex", gap: 20, flexWrap: "wrap", marginBottom: 10 }}>{facts}</div>
            {lines === null ? <div style={{ fontSize: 12, color: "var(--text-muted)" }}>Loading line detail...</div>
                : failed ? <div style={{ fontSize: 12, color: "#ef4444" }}>Line detail could not be loaded.</div>
                : rows.length === 0 ? <div style={{ fontSize: 12, color: "var(--text-muted)" }}>No line detail stored for this event.</div>
                : table}
        </div>
    );
}

// The AI review's shadow score for a ticket (seniors only; the server decides). Read-only: what the model observed and
// what it says a human should check. Reviewers do not see this while the AI is in shadow mode.
function AiReviewPanel({ event }) {
    const [detail, setDetail] = useState(null);
    const [failed, setFailed] = useState(false);
    useEffect(() => { let on = true; api.posfraud.aiScore(event.id).then(d => { if (on) setDetail(d); }).catch(() => { if (on) setFailed(true); }); return () => { on = false; }; }, [event.id]);
    const band = aiBand(event.ai_score);
    return (
        <div data-testid="ai-review-panel" style={{ fontSize: 12, background: "var(--bg-secondary)", border: `1px solid ${band ? band.color : "var(--border)"}55`, borderRadius: 8, padding: 10, marginBottom: 10 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 6 }}>
                <span style={{ fontWeight: 600, color: "var(--text-secondary)" }}>AI review (shadow mode, seniors only)</span>
                <AiScoreBadge score={event.ai_score} verdict={event.ai_verdict} />
                <span style={{ color: "var(--text-muted)" }}>{event.ai_verdict || (band && band.label)}{event.ai_scored_utc ? ` · scored ${fmtTime(event.ai_scored_utc)}` : ""}</span>
            </div>
            {detail ? (<>
                {detail.check_note && <div style={{ marginBottom: 6 }}><b>Check:</b> {detail.check_note}</div>}
                {detail.basis && <div style={{ color: "var(--text-secondary)", whiteSpace: "pre-wrap" }}><b>Observed:</b> {detail.basis}</div>}
                {detail.located === false && <div style={{ color: "#b45309", marginTop: 4 }}>The model did not find the event in the clip; the score reflects the journal only.</div>}
            </>) : failed ? <div style={{ color: "var(--text-muted)" }}>Detail unavailable.</div> : <div style={{ color: "var(--text-muted)" }}>Loading...</div>}
            <div style={{ color: "var(--text-muted)", marginTop: 6 }}>The score does not change the queue or any verdict. Judge the clip as usual.</div>
        </div>
    );
}

function FraudDetail({ event, onDone }) {
    const [notes, setNotes] = useState(event.review_notes || "");
    const [busy, setBusy] = useState(false);
    const [err, setErr] = useState(null);
    const [captureType, setCaptureType] = useState("");   // reason when flagging a bad capture
    const [notViableReason, setNotViableReason] = useState("");
    const [dismissReason, setDismissReason] = useState("");   // why this is not fraud (required with a note)
    // Note templates (27 Oct 2026): fill the box only when it is empty or still an untouched template, never over typed text.
    const fillNotes = (template) => setNotes(cur => (!cur.trim() || NOTE_TEMPLATES.includes(cur)) ? (template || "") : cur);
    const pickDismissReason = (r) => { setDismissReason(r); const t = (DISMISS_REASONS.find(x => x[0] === r) || [])[1]; fillNotes(t || null); };
    const pickNotViableReason = (r) => { setNotViableReason(r); if (NOT_VIABLE_REASON_TEMPLATES[r]) fillNotes(NOT_VIABLE_REASON_TEMPLATES[r]); };
    const notViableTemplatePicker = (
        <select value="" onChange={e => fillNotes(e.target.value)} className="filter-select" data-testid="not-viable-template" style={{ fontSize: 12 }} title="Fills the notes box when it is empty">
            <option value="">Note template...</option>
            {NOT_VIABLE_TEMPLATES.map(t => <option key={t} value={t}>{t}</option>)}
        </select>
    );
    const unfinished = PLACEHOLDER_RE.test(notes);
    const [outcome, setOutcome] = useState({ action_taken: event.action_taken || "", amount_recovered: event.amount_recovered ?? "", actioned_at: event.actioned_at ? String(event.actioned_at).slice(0, 10) : "", hr_reference: event.hr_reference || "" });
    const [outcomeMsg, setOutcomeMsg] = useState(null);
    const saveOutcome = async () => {
        setBusy(true); setErr(null); setOutcomeMsg(null);
        try { await api.posfraud.outcome(event.id, { action_taken: outcome.action_taken || null, amount_recovered: outcome.amount_recovered === "" ? null : Number(outcome.amount_recovered), actioned_at: outcome.actioned_at || null, hr_reference: outcome.hr_reference || null }); setOutcomeMsg("Outcome saved."); onDone && onDone(); }
        catch (e) { setErr(e.message); } finally { setBusy(false); }
    };
    // Re-pull with a corrected time: the reviewer enters the date/time shown on the video at the START of the
    // clip they watched; the edge API derives the recorder's offset, re-pulls, and learns it for the site/camera.
    const receiptDate = event.receipt_local_time ? String(event.receipt_local_time).slice(0, 10) : "";
    const [clipStartDate, setClipStartDate] = useState(receiptDate);
    const [clipStartTime, setClipStartTime] = useState("");
    const [repullAngle, setRepullAngle] = useState("counter");
    const [repullMsg, setRepullMsg] = useState(null);
    // Media (clips + receipt text) is loaded lazily on expand so the list stays fast.
    // null = still loading.
    const [media, setMedia] = useState(null);
    useEffect(() => {
        let alive = true;
        setMedia(null);
        api.posfraud.media(event.id)
            .then(d => { if (alive) setMedia(d.media || []); })
            .catch(() => { if (alive) setMedia([]); });
        return () => { alive = false; };
    }, [event.id]);
    // Senior split: only pos_fraud_senior (posfraud.confirm) can rule on Possibles
    // or lock a confirmed-fraud verdict. Server enforces this too.
    const { hasPermission } = useAuth();
    const isSenior = hasPermission("posfraud.confirm");

    const submit = async (verdict, extra = {}) => {
        if (unfinished && ["dismissed", "footage_not_viable", "capture_issue"].includes(verdict)) { setErr(PLACEHOLDER_HINT); return; }
        if (verdict === "dismissed" && event.status !== "dismissed") {
            if (!extra.dismiss_reason) { setErr("Pick why this ticket is not fraud."); return; }
            if (notes.replace(/\s+/g, "").length < 5) { setErr("Add a note saying what you saw that clears it."); return; }
        }
        if (verdict === "dismissed" && event.has_clip &&
            !window.confirm(event.evidence_retained_on_dismiss
                ? `Mark as NOT fraud? The evidence is kept for ${event.evidence_retention_days} days, then permanently deleted.`
                : "Mark as NOT fraud? All evidence for this event (clips, receipts and any vault copies) will be permanently deleted.")) {
            return;
        }
        if (verdict === "capture_issue" && !extra.capture_issue_type) { setErr("Pick a reason for the capture issue."); return; }
        if (verdict === "footage_not_viable") {
            if (!extra.not_viable_reason) { setErr("Pick why the footage is not viable."); return; }
            const question = extra.not_viable_reason === OLD_CLIP_REASON
                ? "Old clip: the clip is deleted and the event is closed"
                : "Footage not viable: the clip is deleted, the event is closed and the camera is listed for action. Continue?";
            if (!window.confirm(question)) return;
        }
        setBusy(true); setErr(null);
        try {
            await api.posfraud.review(event.id, { verdict, notes, ...extra });
            onDone();
        } catch (e) {
            setErr(e.message || "Failed to save review");
            setBusy(false);
        }
    };

    // Which cameras this event has clips for (from the gallery labels), for the re-pull camera choice.
    const clipAngles = useMemo(() => {
        const found = new Set((media || []).filter(m => m.kind === "clip").map(m => /front/i.test(m.label || "") ? "front" : "counter"));
        return found.size ? [...found] : ["counter"];
    }, [media]);
    useEffect(() => { if (!clipAngles.includes(repullAngle)) setRepullAngle(clipAngles[0]); }, [clipAngles, repullAngle]);

    const repull = async () => {
        if (!clipStartDate || !clipStartTime) { setErr("Enter the date and time shown at the start of the clip."); return; }
        if (unfinished && event.status !== "capture_issue") { setErr(PLACEHOLDER_HINT); return; }   // the re-pull also files a capture issue with this note
        const time = clipStartTime.length === 5 ? clipStartTime + ":00" : clipStartTime;
        // Sanity check against the sale time before anything is sent: a wrong year, AM/PM or a day/month swap
        // lands hours or days away. Over 12 h is refused outright (the server would refuse it too); over 30 min
        // asks the reviewer to confirm, naming the gap.
        if (event.receipt_local_time) {
            const entered = new Date(`${clipStartDate}T${time}`), sale = new Date(String(event.receipt_local_time).slice(0, 19));
            const diffMin = Math.round((entered - sale) / 60000);
            if (Number.isFinite(diffMin)) {
                const gap = `${Math.floor(Math.abs(diffMin) / 60)} h ${Math.abs(diffMin) % 60} min ${diffMin < 0 ? "before" : "after"} the sale time (${fmtWallTime(event.receipt_local_time)})`;
                const dateDiffers = clipStartDate !== receiptDate;
                if (Math.abs(diffMin) > 12 * 60) {
                    setErr(`The time you entered is ${gap}. That cannot be the start of this clip: check the date, the year and AM/PM.`);
                    return;
                }
                if (Math.abs(diffMin) > 30 || dateDiffers) {
                    const why = dateDiffers ? `The date you entered (${clipStartDate}) differs from the receipt date (${receiptDate}); check day and month are the right way round.` : "";
                    if (!window.confirm(`The time you entered is ${gap}. ${why}\nCheck the date and AM/PM on the video. Send the re-pull anyway?`)) return;
                }
            }
        }
        setBusy(true); setErr(null); setRepullMsg(null);
        try {
            const r = await api.posfraud.replacement(event.id, {
                angle: repullAngle, observed_clip_start_nvr_local: `${clipStartDate}T${time}`, reason: captureType || "Sale not in clip",
            });
            const req = r.requests && r.requests[0];
            const off = req ? req.recorder_offset_seconds : null;
            setRepullMsg(`Re-pull requested${off != null ? ` with the recorder ${off >= 0 ? "ahead" : "behind"} by ${Math.abs(off)} s` : ""}. The corrected clip replaces this one when it lands; the original stays in the gallery.`);
            // The event moves to Capture Issues while the corrected clip is fetched; the clip's arrival returns it to the queue.
            if (event.status !== "capture_issue") {
                await api.posfraud.review(event.id, { verdict: "capture_issue", notes, capture_issue_type: captureType || "Sale not in clip", request_clip: false });
            }
            onDone();
        } catch (e) {
            setErr(REPULL_ERRORS[e.message] || e.message || "Re-pull failed");
            setBusy(false);
        }
    };

    // Failed edge capture (no clip): ask the site's agent to pull the same window again. The ticket stays where it is;
    // the clip's arrival returns a capture issue to the queue.
    const recapture = async () => {
        setBusy(true); setErr(null); setRepullMsg(null);
        try {
            const r = await api.posfraud.recapture(event.id, { reason: notes ? notes.slice(0, 120) : "capture_failed" });
            const n = (r.requests || []).length, skipped = (r.skipped || []).length;
            setRepullMsg(`Capture re-requested for ${n} camera${n === 1 ? "" : "s"}${skipped ? ` (${skipped} skipped: footage no longer on the recorder)` : ""}. The clip appears here when the site uploads it.`);
            setBusy(false);
        } catch (e) {
            setErr(REPULL_ERRORS[e.message] || e.message || "Re-pull failed");
            setBusy(false);
        }
    };

    const purgeClip = async () => {
        if (!window.confirm("Permanently delete this clip from storage? The event stays for the record.")) return;
        setBusy(true); setErr(null);
        try {
            await api.posfraud.deleteClip(event.id);
            onDone();
        } catch (e) {
            setErr(e.message || "Failed to delete clip");
            setBusy(false);
        }
    };

    const btn = (bg) => btnStyle(bg, busy);
    const notFraudBlocked = !hasFootage(event);
    const barcodeNote = barcodeGuardNote(event.narrative);
    // Edge ticket whose capture failed: requested by the new capture system, no clip landed, still within recorder retention.
    const canRecapture = !event.has_clip && event.has_capture_request && (ageDays(event.receipt_local_time) ?? 0) <= REPULL_MAX_AGE_DAYS;
    const recaptureBlock = canRecapture && (
        <div style={{ marginTop: 10, paddingTop: 8, borderTop: "1px dashed var(--border)" }}>
            <div style={{ fontSize: 12, fontWeight: 600, color: "var(--text-secondary)", marginBottom: 4 }}>No clip captured?</div>
            <div style={{ fontSize: 11, color: "var(--text-muted)", marginBottom: 6 }}>
                The site could not capture this sale. It is retried automatically (2 hours after a recorder or network failure,
                then 12 hours after a second); nothing to do here. Retry now only if a recorder problem has just been fixed.
            </div>
            {/* Secondary: retries are automatic, this is the manual override. */}
            <button onClick={recapture} disabled={busy} style={{ ...btn("transparent"), color: "var(--text-secondary)", border: "1px solid var(--border)", fontSize: 11 }}><RotateCcw size={12} /> Retry capture now</button>
            {repullMsg && <div style={{ fontSize: 11, color: "var(--text-secondary)", marginTop: 6 }}>{repullMsg}</div>}
        </div>
    );

    return (<>
        {repeatCountOf(event) != null && <div data-testid="repeat-detail" style={{ marginTop: 12 }}><RepeatBadge event={event} /></div>}
        {isNewType(event.event_type) && <DetectorDetail event={event} />}
        <div style={{ padding: "12px 0", display: "grid", gridTemplateColumns: "minmax(0, 1.3fr) minmax(0, 1fr)", gap: 20 }}>
            {/* Left: media — one or more labelled clips, plus receipts when present */}
            <div>
                {(() => {
                    if (media === null) return (
                        <div style={{ padding: 20, background: "var(--bg-secondary)", borderRadius: 8, border: "1px dashed var(--border)", textAlign: "center", color: "var(--text-muted)", fontSize: 13 }}>
                            Loading evidence...
                        </div>
                    );
                    const clips = media.filter(m => m.kind === "clip");
                    const receipts = media.filter(m => m.kind === "receipt");
                    return (<>
                        <div style={{ fontSize: 12, fontWeight: 600, color: "var(--text-secondary)", marginBottom: 6 }}>
                            Footage{clips.length > 1 ? ` (${clips.length} clips)` : ""}
                        </div>
                        {clips.length > 0 && (
                            <div style={{ fontSize: 10, color: "var(--text-muted)", marginBottom: 6 }}>
                                Event time (POS journal): {fmtWallTime(event.receipt_local_time)} — the clip's burned-in timestamp may differ.
                            </div>
                        )}
                        {clips.length > 0 && clips.every(c => /front/i.test(c.label || "")) && (
                            <div data-testid="front-only-clip" style={{ fontSize: 11, fontWeight: 600, color: "#f59e0b", marginBottom: 6 }}>
                                Front-of-counter angle only: there is no counter (till) clip for this ticket.
                            </div>
                        )}
                        {clips.length > 0 ? clips.map((c, i) => (
                            <div key={i} style={{ marginBottom: 12 }}>
                                {c.label && <div style={{ fontSize: 11, fontWeight: 600, color: "var(--text-muted)", marginBottom: 4 }}>{c.label}</div>}
                                <VideoPlayer src={c.playbackUrl}
                                    onDownload={c.downloadUrl ? () => { window.location.href = c.downloadUrl; } : null} />
                            </div>
                        )) : (
                            <div style={{ padding: 20, background: "var(--bg-secondary)", borderRadius: 8, border: "1px dashed var(--border)", textAlign: "center", color: "var(--text-muted)", fontSize: 13 }}>
                                {clipStateOf(event).long}
                            </div>
                        )}

                        {receipts.length > 0 && (
                            <div style={{ marginTop: 12 }}>
                                <div style={{ fontSize: 12, fontWeight: 600, color: "var(--text-secondary)", marginBottom: 6 }}>
                                    Receipts{receipts.length > 1 ? ` (${receipts.length})` : ""}
                                </div>
                                <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
                                    {receipts.map((r, i) => (
                                        <div key={i} style={{ maxWidth: 340, ...(r.highlight ? { padding: 6, borderRadius: 8, background: "#f59e0b18", outline: "2px solid #f59e0b" } : {}) }}>
                                            {r.label && (
                                                <div style={{ fontSize: 11, fontWeight: 600, color: r.highlight ? "#b45309" : "var(--text-muted)", marginBottom: 4 }}>
                                                    {r.highlight && <span style={{ background: "#f59e0b", color: "#fff", borderRadius: 4, padding: "1px 5px", marginRight: 6, fontSize: 10, fontWeight: 700 }}>FLAGGED TXN</span>}
                                                    {r.label}
                                                </div>
                                            )}
                                            {r.text != null ? (
                                                <pre style={{ fontSize: 11, lineHeight: 1.4, fontFamily: "ui-monospace, Consolas, monospace", whiteSpace: "pre-wrap", background: "var(--bg-secondary)", border: r.highlight ? "1px solid #f59e0b" : "1px solid var(--border)", borderRadius: 6, padding: 10, margin: 0, maxHeight: 340, overflow: "auto" }}>{r.text}</pre>
                                            ) : r.imageUrl ? (
                                                <a href={r.downloadUrl || r.imageUrl} target="_blank" rel="noreferrer" onClick={e => e.stopPropagation()}>
                                                    <img src={r.imageUrl} alt={r.label || "Receipt"} style={{ maxWidth: 260, borderRadius: 6, border: "1px solid var(--border)" }} />
                                                </a>
                                            ) : (
                                                <div style={{ fontSize: 12, color: "var(--text-muted)" }}>Receipt unavailable</div>
                                            )}
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}

                        {event.status === "dismissed" && (clips.length > 0 || receipts.length > 0) && event.evidence_purge_after && (
                            <div style={{ marginTop: 8, fontSize: 12, color: "var(--text-secondary)" }}>
                                Evidence kept after the Not fraud call until {new Date(event.evidence_purge_after).toLocaleString("en-AU", { dateStyle: "medium" })}, then removed automatically.
                            </div>
                        )}
                        {event.status === "dismissed" && event.evidence_held_not_shown && event.evidence_purge_after && (
                            <div style={{ marginTop: 8, fontSize: 12, color: "var(--text-secondary)" }} data-testid="evidence-held">
                                Evidence kept in storage after the Not fraud call until {new Date(event.evidence_purge_after).toLocaleString("en-AU", { dateStyle: "medium" })}, then removed automatically. It is not played on this page.
                            </div>
                        )}
                        {event.status === "dismissed" && (clips.length > 0 || receipts.length > 0 || event.evidence_held_not_shown) && (
                            <button onClick={purgeClip} disabled={busy}
                                style={{ marginTop: 8, ...btn("transparent"), color: "#ef4444", border: "1px solid #ef444455", background: "transparent" }}>
                                <Trash2 size={14} /> Delete evidence from storage
                            </button>
                        )}
                    </>);
                })()}
            </div>

            {/* Right: narrative + review */}
            <div>
                {event.ai_score != null && <AiReviewPanel event={event} />}
                <div style={{ fontSize: 12, fontWeight: 600, color: "var(--text-secondary)", marginBottom: 6 }}>AI narrative</div>
                <div style={{ fontSize: 13, lineHeight: 1.5, whiteSpace: "pre-wrap", background: "var(--bg-secondary)", borderRadius: 8, padding: 12, marginBottom: 6 }}>
                    {event.narrative || event.description || "No narrative."}
                </div>
                {barcodeNote && (
                    <div data-barcode-guard="1" style={{ display: "flex", alignItems: "flex-start", gap: 6, fontSize: 12, color: "#b45309", background: "#f59e0b18", border: "1px solid #f59e0b55", borderRadius: 6, padding: "6px 8px", marginBottom: 6 }}>
                        <AlertTriangle size={13} style={{ flexShrink: 0, marginTop: 1 }} />
                        <span><b>Barcode guard:</b> {barcodeNote}. That line is not counted in the value.</span>
                    </div>
                )}
                <div style={{ fontSize: 11, color: "var(--text-muted)", marginBottom: 12 }}>
                    <span style={{ fontFamily: "monospace", fontWeight: 700, color: "var(--text-secondary)" }}>{ticketRef(event.id)}</span> · Reg {event.register_id ?? "--"}
                    {event.clerk_number != null ? ` · clerk ${event.clerk_number}` : ""}
                    {event.transaction_number ? ` · txn ${event.transaction_number}` : " · no txn (camera-led)"}
                    {event.inventory_code ? ` · ${event.inventory_code}` : ""}
                </div>

                {event.reviewed_by && (
                    <div style={{ fontSize: 12, color: "var(--text-secondary)", marginBottom: 10 }}>
                        <StatusBadge status={event.status} notViable={!!event.not_viable_reason} /> by {event.reviewed_by} · {fmtTime(event.reviewed_at)}
                        {event.dismiss_reason ? <div style={{ marginTop: 4 }}>Reason: {event.dismiss_reason}</div> : null}
                        {event.review_notes ? <div style={{ marginTop: 4, fontStyle: "italic" }}>"{event.review_notes}"</div> : null}
                    </div>
                )}
                <ReviewTrail event={event} />

                {["possible", "reviewed", "confirmed", "finalised"].includes(event.status) && <StaffPanel event={event} isSenior={isSenior} onDone={onDone} />}

                {err && <div style={{ color: "#ef4444", fontSize: 12, marginBottom: 8 }}>{err}</div>}

                {event.status === "confirmed" ? (
                    <div>
                        <div style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 12, color: "var(--text-secondary)" }}>
                            <Lock size={13} /> Locked as confirmed fraud. Evidence is retained permanently.
                        </div>
                        {isSenior && <OutcomePanel outcome={outcome} setOutcome={setOutcome} save={saveOutcome} busy={busy} msg={outcomeMsg} />}
                        {isSenior && (
                            <div style={{ marginTop: 12, paddingTop: 10, borderTop: "1px solid var(--border)" }}>
                                <div style={{ fontSize: 12, fontWeight: 600, color: "var(--text-secondary)", marginBottom: 6 }}>
                                    Real-world outcome
                                </div>
                                <textarea value={notes} onChange={e => setNotes(e.target.value)}
                                    placeholder="What was done about this — e.g. 'Employee terminated 20/07/2026, police report #12345'..."
                                    rows={2}
                                    style={{ width: "100%", fontSize: 13, padding: 8, borderRadius: 6, border: "1px solid var(--border)", background: "var(--bg-main)", color: "var(--text-primary)", resize: "vertical", marginBottom: 8 }} />
                                <button onClick={() => submit("finalised")} disabled={busy} style={btn("#15803d")}>
                                    <CheckCircle2 size={14} /> Mark as finalised (dealt with)
                                </button>
                                <div style={{ fontSize: 11, color: "var(--text-muted)", marginTop: 6 }}>
                                    Use once the matter is closed in real life (staff actioned etc.). Evidence is retained.
                                </div>
                            </div>
                        )}
                    </div>
                ) : event.status === "finalised" ? (
                    <div>
                        <div style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 12, color: "#15803d" }}>
                            <CheckCircle2 size={13} /> Finalised — dealt with in real life. Evidence retained.
                        </div>
                        {isSenior && <OutcomePanel outcome={outcome} setOutcome={setOutcome} save={saveOutcome} busy={busy} msg={outcomeMsg} />}
                    </div>
                ) : event.status === "dismissed" ? (event.not_viable_reason ? (
                    <div style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 12, color: "#f59e0b" }}>
                        <Camera size={13} /> Footage not viable: {event.not_viable_reason}. Clip deleted; the camera is listed on the Fraud Report until actioned.
                    </div>) : null)
                : event.status === "capture_issue" ? (<>
                    {/* Capture-issue worklist: read the reason + comment, action it, then move it on. */}
                    <div style={{ background: "#8b5cf618", border: "1px solid #8b5cf655", borderRadius: 8, padding: 10, marginBottom: 10 }}>
                        <div style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 12, fontWeight: 700, color: "#8b5cf6", marginBottom: 4 }}>
                            <Camera size={14} /> Capture issue{event.capture_issue_type ? `: ${event.capture_issue_type}` : ""}
                        </div>
                        <div style={{ fontSize: 12, color: "var(--text-secondary)" }}>
                            {event.review_notes ? `"${event.review_notes}"` : "No comment provided."}
                        </div>
                        {event.clip_request_id && (
                            <div style={{ fontSize: 11, marginTop: 6, color: event.clip_request_status === "completed" ? "#22c55e" : event.clip_request_status === "failed" ? "#ef4444" : "var(--text-muted)" }}>
                                <Video size={12} style={{ verticalAlign: "middle" }} /> 11-min clip re-pull: {event.clip_request_status || "requested"}
                                {event.clip_request_status === "completed" ? " (plays on the left)" : event.clip_request_status === "failed" ? " (pull failed)" : "..."}
                            </div>
                        )}
                    </div>
                    <textarea value={notes} onChange={e => setNotes(e.target.value)} placeholder="Note before you action this (optional)..." rows={2}
                        style={{ width: "100%", fontSize: 13, padding: 8, borderRadius: 6, border: "1px solid var(--border)", background: "var(--bg-main)", color: "var(--text-primary)", resize: "vertical", marginBottom: 8 }} />
                    {unfinished && <div data-testid="notes-placeholder" style={{ fontSize: 11, color: "#b45309", marginBottom: 8 }}>{PLACEHOLDER_HINT} Footage not viable stays off until then.</div>}
                    <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                        <button onClick={() => submit("requeue")} disabled={busy} style={btn("#3b82f6")}><RotateCcw size={14} /> Return to Queue</button>
                        <button onClick={() => submit("capture_resolved")} disabled={busy} style={btn("#14b8a6")}><Archive size={14} /> Resolve</button>
                    </div>
                    {/* No Not fraud here: a capture issue is about the footage, not a verdict. Judge it from the Queue once a clip is in. */}
                    <div style={{ fontSize: 11, color: "var(--text-muted)", marginTop: 6 }}>
                        Not fraud is not offered on a capture issue: return it to the Queue to judge the clip{notFraudBlocked ? " once the footage is in" : ""}, or close it as Footage not viable.
                    </div>
                    {recaptureBlock}
                    <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center", marginTop: 10, paddingTop: 8, borderTop: "1px dashed var(--border)" }}>
                        <span style={{ fontSize: 12, fontWeight: 600, color: "var(--text-secondary)" }}>Footage not viable?</span>
                        <select value={notViableReason} onChange={e => pickNotViableReason(e.target.value)} className="filter-select" style={{ fontSize: 12 }}>
                            <option value="">Reason...</option>
                            {NOT_VIABLE_REASONS.map(r => <option key={r} value={r}>{r}</option>)}
                        </select>
                        {notViableTemplatePicker}
                        <button onClick={() => submit("footage_not_viable", { not_viable_reason: notViableReason })} disabled={busy || !notViableReason || unfinished} title={unfinished ? PLACEHOLDER_HINT : undefined} style={btn("#f59e0b")}><Trash2 size={14} /> Footage not viable</button>
                    </div>
                </>) : event.status === "capture_resolved" ? (
                    <div style={{ fontSize: 12, color: "var(--text-secondary)" }}>
                        <div style={{ display: "flex", alignItems: "center", gap: 6, color: "#14b8a6", marginBottom: 6 }}>
                            <Archive size={13} /> Capture issue resolved{event.capture_issue_type ? ` (${event.capture_issue_type})` : ""}.
                        </div>
                        <button onClick={() => submit("requeue")} disabled={busy}
                            style={{ ...btn("transparent"), color: "var(--text-secondary)", border: "1px solid var(--border)", background: "transparent" }}>
                            <RotateCcw size={14} /> Return to Queue
                        </button>
                    </div>
                ) : (<>
                    {isPossible(event.status) && (
                        <div style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 12, color: "#f59e0b", marginBottom: 8 }}>
                            <Lock size={13} /> Evidence is vaulted (protected copy in secondary storage) until a final verdict.
                        </div>
                    )}
                    <textarea
                        value={notes}
                        onChange={e => setNotes(e.target.value)}
                        placeholder="Review notes / capture comment..."
                        rows={2}
                        style={{ width: "100%", fontSize: 13, padding: 8, borderRadius: 6, border: "1px solid var(--border)", background: "var(--bg-main)", color: "var(--text-primary)", resize: "vertical", marginBottom: 8 }}
                    />
                    {unfinished && <div data-testid="notes-placeholder" style={{ fontSize: 11, color: "#b45309", marginBottom: 8 }}>{PLACEHOLDER_HINT} Not fraud, Footage not viable and Flag capture issue stay off until then.</div>}

                    {isPossible(event.status) && !isSenior ? (
                        <div style={{ fontSize: 12, color: "var(--text-secondary)", marginBottom: 8 }}>
                            Awaiting senior assessment — fraud verdicts on Possibles need the POS Fraud Senior role. You can still flag a capture problem below.
                        </div>
                    ) : (<>
                        <div style={{ fontSize: 11, color: notFraudBlocked ? "#b45309" : "var(--text-muted)", marginBottom: 8 }} data-testid={notFraudBlocked ? "not-fraud-blocked" : undefined}>
                            {notFraudBlocked ? NOT_FRAUD_NEEDS_CLIP : event.evidence_retained_on_dismiss
                                ? `"Not fraud" needs a reason and a note. It keeps the evidence for ${event.evidence_retention_days} days, then deletes it.`
                                : "\"Not fraud\" needs a reason and a note, and permanently deletes all evidence for this event."}
                        </div>
                        {!notFraudBlocked && event.status !== "dismissed" && (
                            <select value={dismissReason} onChange={e => pickDismissReason(e.target.value)} className="filter-select" data-testid="dismiss-reason" style={{ fontSize: 12, marginBottom: 8, maxWidth: "100%" }}>
                                <option value="">Why is it not fraud?...</option>
                                {DISMISS_REASONS.map(([r]) => <option key={r} value={r}>{r}</option>)}
                            </select>
                        )}
                        <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginBottom: 10 }}>
                            {/* Queue (skimmer): bubble up or clear. Possible (audit senior): lock as fraud or clear. */}
                            {isPossible(event.status)
                                ? <button onClick={() => submit("confirmed")} disabled={busy} style={btn("#ef4444")}><Check size={14} /> Confirm fraud</button>
                                : <button onClick={() => submit("possible")} disabled={busy} style={btn("#f59e0b")}><Flag size={14} /> Possible fraud</button>}
                            <button onClick={() => submit("dismissed", event.status === "dismissed" ? {} : { dismiss_reason: dismissReason })}
                                disabled={busy || notFraudBlocked || unfinished || (event.status !== "dismissed" && (!dismissReason || notes.replace(/\s+/g, "").length < 5))}
                                title={notFraudBlocked ? NOT_FRAUD_NEEDS_CLIP : unfinished ? PLACEHOLDER_HINT : (event.status !== "dismissed" && (!dismissReason || notes.replace(/\s+/g, "").length < 5)) ? "Pick a reason and write a note first" : undefined}
                                style={notFraudBlocked || unfinished || (event.status !== "dismissed" && (!dismissReason || notes.replace(/\s+/g, "").length < 5)) ? { ...btn("#6b7280"), opacity: 0.45, cursor: "not-allowed" } : btn("#6b7280")}><X size={14} /> Not fraud</button>
                        </div>
                    </>)}

                    {/* Capture problem: any reviewer can route an unusable capture to a tech (evidence kept). */}
                    <div style={{ marginTop: 4, paddingTop: 10, borderTop: "1px solid var(--border)" }}>
                        <div style={{ fontSize: 12, fontWeight: 600, color: "var(--text-secondary)", marginBottom: 6 }}>Capture problem?</div>
                        <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center" }}>
                            <select value={captureType} onChange={e => setCaptureType(e.target.value)} className="filter-select" style={{ fontSize: 12 }}>
                                <option value="">Reason...</option>
                                {CAPTURE_REASONS.map(r => <option key={r} value={r}>{r}</option>)}
                            </select>
                            <select value="" onChange={e => fillNotes(e.target.value)} className="filter-select" data-testid="capture-template" style={{ fontSize: 12 }} title="Fills the notes box when it is empty">
                                <option value="">Note template...</option>
                                {CAPTURE_ISSUE_TEMPLATES.map(t => <option key={t} value={t}>{t}</option>)}
                            </select>
                            <button onClick={() => submit("capture_issue", { capture_issue_type: captureType, request_clip: false })} disabled={busy || !captureType || unfinished} title={unfinished ? PLACEHOLDER_HINT : undefined} style={btn("#8b5cf6")}>
                                <Camera size={14} /> Flag capture issue
                            </button>
                        </div>
                        <div style={{ fontSize: 11, color: "var(--text-muted)", marginTop: 4 }}>
                            Uses the notes box above as the comment. Evidence is kept; the item moves to the Capture Issues tab.
                        </div>
                        {!isPossible(event.status) && (
                            <div style={{ marginTop: 10, paddingTop: 8, borderTop: "1px dashed var(--border)" }}>
                                <div style={{ fontSize: 12, fontWeight: 600, color: "var(--text-secondary)", marginBottom: 4 }}>Footage not viable?</div>
                                <div style={{ fontSize: 11, color: "var(--text-muted)", marginBottom: 6 }}>The camera itself is the problem (dirty, aimed wrong, out of focus), or the clip is too old to re-pull. Deletes the footage instead of re-pulling and closes the event. A camera reason lists the camera for action; "Old clip" does not.</div>
                                <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center" }}>
                                    <select value={notViableReason} onChange={e => pickNotViableReason(e.target.value)} className="filter-select" style={{ fontSize: 12 }}>
                                        <option value="">Reason...</option>
                                        {NOT_VIABLE_REASONS.map(r => <option key={r} value={r}>{r}</option>)}
                                    </select>
                                    {notViableTemplatePicker}
                                    <button onClick={() => submit("footage_not_viable", { not_viable_reason: notViableReason })} disabled={busy || !notViableReason || unfinished} title={unfinished ? PLACEHOLDER_HINT : undefined} style={btn("#f59e0b")}>
                                        <Trash2 size={14} /> Footage not viable
                                    </button>
                                </div>
                            </div>
                        )}

                        {/* A ticket returned to the queue after a failed edge capture still has no clip: retry it here. */}
                        {event.status === "new" && recaptureBlock}

                        {/* Sale not in the clip: re-pull with the time shown at the start of the clip. The edge API works out the
                            recorder's offset from it and applies it to this site's camera from now on. */}
                        {event.has_clip && (ageDays(event.receipt_local_time) ?? 0) > REPULL_MAX_AGE_DAYS && (
                            <div style={{ marginTop: 10, paddingTop: 8, borderTop: "1px dashed var(--border)" }}>
                                <div style={{ fontSize: 12, fontWeight: 600, color: "var(--text-secondary)", marginBottom: 4 }}>Sale not in the clip?</div>
                                <div style={{ fontSize: 12, color: "var(--text-secondary)" }}>
                                    This clip cannot be re-pulled due to age: the recorder no longer has footage from {fmtWallTime(event.receipt_local_time)}.
                                    If it is not usable, select <b>Footage not viable</b> and choose <b>{OLD_CLIP_REASON}</b>.
                                </div>
                            </div>
                        )}
                        {event.has_clip && (ageDays(event.receipt_local_time) ?? 0) <= REPULL_MAX_AGE_DAYS && !event.has_capture_request && (
                            <div style={{ marginTop: 10, paddingTop: 8, borderTop: "1px dashed var(--border)" }}>
                                <div style={{ fontSize: 12, fontWeight: 600, color: "var(--text-secondary)", marginBottom: 4 }}>Sale not in the clip?</div>
                                <div style={{ fontSize: 12, color: "var(--text-secondary)" }}>
                                    This clip came from the old capture system and cannot be re-pulled from this page. Use <b>Flag capture issue</b> above
                                    with the reason "Sale not in clip" and a tech will re-pull it.
                                </div>
                            </div>
                        )}
                        {event.has_clip && (ageDays(event.receipt_local_time) ?? 0) <= REPULL_MAX_AGE_DAYS && event.has_capture_request && (
                            <div style={{ marginTop: 10, paddingTop: 8, borderTop: "1px dashed var(--border)" }}>
                                <div style={{ fontSize: 12, fontWeight: 600, color: "var(--text-secondary)", marginBottom: 4 }}>Sale not in the clip?</div>
                                <div style={{ fontSize: 11, color: "var(--text-muted)", marginBottom: 6 }}>
                                    Enter the date and time shown on the video at the <b>start</b> of the clip, then re-pull. Event time (POS): {fmtWallTime(event.receipt_local_time)}.
                                </div>
                                <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center" }}>
                                    {clipAngles.length > 1 && (
                                        <select value={repullAngle} onChange={e => setRepullAngle(e.target.value)} className="filter-select" style={{ fontSize: 12 }}>
                                            {clipAngles.map(a => <option key={a} value={a}>{a === "front" ? "Front-of-counter clip" : "Counter clip"}</option>)}
                                        </select>
                                    )}
                                    <input type="date" value={clipStartDate} onChange={e => setClipStartDate(e.target.value)} className="filter-select" style={{ fontSize: 12 }} />
                                    <input type="time" step="1" value={clipStartTime} onChange={e => setClipStartTime(e.target.value)} className="filter-select" style={{ fontSize: 12 }} />
                                    <button onClick={repull} disabled={busy || !clipStartDate || !clipStartTime} style={btn("#0ea5e9")}>
                                        <RotateCcw size={14} /> Re-pull with corrected time
                                    </button>
                                </div>
                                {repullMsg && <div style={{ fontSize: 11, color: "var(--text-secondary)", marginTop: 6 }}>{repullMsg}</div>}
                            </div>
                        )}
                    </div>
                </>)}
            </div>
        </div>
    </>);
}
